import { HttpClient } from '@angular/common/http';
import {Injectable} from '@angular/core';
import {firstValueFrom, map, Observable} from 'rxjs';
import {Pagination, PaginationResponse} from '@models/pagination.model';
import {firstValueFromSafe} from '@shared/utils/empty-error';
import {environment} from "../../../../environments/environment";

@Injectable()
export class PaginatorService<T> {

    protected urlToEntity: string;

    constructor(protected http: HttpClient) {
        this.urlToEntity = `${environment.baseApiUrl}v2/${this.domainEntity}`;
    }

    public setParent(id: number) {
        this.urlToEntity = `${environment.baseApiUrl}v2/${this.domainEntity}/${id}/${this.subdomainEntity}`;
    }

    protected get domainEntity() {
        return ''
    };

    protected get subdomainEntity() {
        return ''
    };

    /**
     * For display-only callers (dropdown options, lookups): a call the interceptor already handled
     * renders an empty list instead of an EmptyError.
     * Never use it where an empty result changes what gets saved or shown as loaded: use listAllStrict().
     */
    async listAll(): Promise<PaginationResponse<T>> {
        let result = this.listPaginated(new Pagination(9999))
        return firstValueFromSafe(result, {data: [], pagination: undefined})
    }

    /**
     * Same request as listAll(), but a failed call rejects (EmptyError after the interceptor handled it)
     * so the caller cannot mistake it for an empty list. EmptyError is already dropped by AppErrorHandler.
     */
    async listAllStrict(): Promise<PaginationResponse<T>> {
        return firstValueFrom(this.listPaginated(new Pagination(9999)))
    }

    listPaginated(pagination: Pagination = new Pagination()): Observable<PaginationResponse<T>> {
        if (pagination.filter != undefined && Object.keys(pagination.filter).length === 0) {
            pagination.filter = undefined;
        }

        const paginationParams = `size=${pagination.size}&start=${pagination.start * pagination.size}`;
        const sortParams = pagination.sort ? `&sort=${pagination.sort.prop}&order=${pagination.sort.order}` : '';
        const filterParams = pagination.filter ?
            `&${Object.keys(pagination.filter)
                .filter(key => pagination.filter[key] !== null && pagination.filter[key] !== undefined)
                .map(key => `${key}=${pagination.filter[key]}`)
                .join('&')}`
            : '';

        return this.http.get<PaginationResponse<T>>(this.urlToEntity + '?' + paginationParams + filterParams + sortParams)
            .pipe(
                map((response) => {
                    return {
                        data: !response?.data?.length ? [] : response.data,
                        pagination: response.pagination
                    }
                })
            )
    }
}
